import { create } from 'zustand';

/**
 * Unified Questionnaire Store for managing NC routing and branching answers
 * @typedef {Object} QuestionnaireStore
 * @property {string} ncStatus - NC status answer ("at_nc", "nc_below12", "below_nc", "not_nc")
 * @property {Object} branchAnswers - Branch-specific answers keyed by question ID
 * @property {string} currentBranch - Current question branch ("PLAIN_LANGUAGE" or "NC_BRANCH")
 * @property {number} currentQuestionIndex - Current question index within branch
 * @property {function} setNcStatus - Set NC status answer
 * @property {function} setBranchAnswer - Set answer for specific question ID
 * @property {function} setCurrentBranch - Set current question branch
 * @property {function} setCurrentQuestionIndex - Set current question index
 * @property {function} reset - Reset all answers
 * @property {function} isNcStatusComplete - Check if NC status is answered
 * @property {function} isBranchComplete - Check if current branch is complete
 * @property {function} isComplete - Check if entire questionnaire is complete
 */

export const useTriageStore = create((set, get) => ({
  // NC Status answer
  ncStatus: null,
  
  // Branch-specific answers (keyed by question ID)
  branchAnswers: {},
  
  // Current state
  currentBranch: null,
  currentQuestionIndex: 0,
  
  // Actions
  setNcStatus: (status) => {
    set({ ncStatus: status });
  },
  
  setBranchAnswer: (questionId, value) => {
    set((state) => ({
      branchAnswers: {
        ...state.branchAnswers,
        [questionId]: value
      }
    }));
  },
  
  setCurrentBranch: (branch) => {
    set({ currentBranch: branch, currentQuestionIndex: 0 });
  },
  
  setCurrentQuestionIndex: (index) => {
    set({ currentQuestionIndex: index });
  },
  
  reset: () => {
    set({ 
      ncStatus: null, 
      branchAnswers: {}, 
      currentBranch: null, 
      currentQuestionIndex: 0 
    });
  },
  
  isNcStatusComplete: () => {
    const { ncStatus } = get();
    return ncStatus !== null;
  },
  
  isBranchComplete: () => {
    const { currentBranch, branchAnswers } = get();
    if (!currentBranch) return false;
    
    // Import question logic to get required questions
    const { getQuestionsForBranch } = require('../questionnaireLogic');
    const questions = getQuestionsForBranch(currentBranch);
    
    return questions.every(question => branchAnswers[question.id] !== undefined);
  },
  
  isComplete: () => {
    const { isNcStatusComplete, isBranchComplete } = get();
    return isNcStatusComplete() && isBranchComplete();
  },
  
  // Legacy compatibility - map to new structure
  get answers() {
    const { branchAnswers } = get();
    // Convert branch answers to array format for legacy compatibility
    return Object.values(branchAnswers);
  },
  
  setAnswer: (index, value) => {
    const { currentBranch } = get();
    if (!currentBranch) return;
    
    const { getQuestionsForBranch } = require('../questionnaireLogic');
    const questions = getQuestionsForBranch(currentBranch);
    const question = questions[index];
    
    if (question) {
      get().setBranchAnswer(question.id, value);
    }
  }
}));



